import { bind } from 'bind-event-listener';
import isEqual from 'lodash/isEqual';
import uniqueId from 'lodash/uniqueId';
import type { IntlShape } from 'react-intl';

import { getDocument } from '@atlaskit/browser-apis';
import { isSSR } from '@atlaskit/editor-common/core-utils';
import {
	messages,
	EmojiSharedCssClassName,
	defaultEmojiHeight,
} from '@atlaskit/editor-common/emoji';
import { logException } from '@atlaskit/editor-common/monitoring';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { VanillaTooltip } from '@atlaskit/editor-common/vanilla-tooltip';
import { isOfflineMode } from '@atlaskit/editor-plugin-connectivity';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { DOMSerializer } from '@atlaskit/editor-prosemirror/model';
import type { NodeView } from '@atlaskit/editor-prosemirror/view';
import { emojiIdToEmoji } from '@atlaskit/emoji/emoji-id-to-emoji';
import type {
	EmojiDescription,
	ImageRepresentation,
	MediaApiRepresentation,
	SpriteRepresentation,
	EmojiProvider,
	EmojiRepresentation,
	OptionalEmojiDescriptionWithVariations,
} from '@atlaskit/emoji/types';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { token } from '@atlaskit/tokens';

import type { EmojiPlugin } from '../emojiPluginType';
import type { EmojiNodeDataProvider } from '../pm-plugins/providers/EmojiNodeDataProvider';

import { emojiToDom } from './emojiNodeSpec';

const SINGLE_EMOJI_REGEX =
	// Regular expression to match a single emoji character
	/^(\p{Emoji_Presentation}(?:[\u{1F3FB}-\u{1F3FF}])?|\p{Extended_Pictographic}\u{FE0F}(?:[\u{1F3FB}-\u{1F3FF}])?(?:\u{200D}\p{Extended_Pictographic}\u{FE0F}?(?:[\u{1F3FB}-\u{1F3FF}])?)*|\p{Extended_Pictographic}\u{FE0F}?(?:[\u{1F3FB}-\u{1F3FF}])?(?:\u{200D}\p{Extended_Pictographic}\u{FE0F}?(?:[\u{1F3FB}-\u{1F3FF}])?)+|\p{Regional_Indicator}\p{Regional_Indicator})$/u;

interface Params {
	api: ExtractInjectionAPI<EmojiPlugin> | undefined;
	emojiNodeDataProvider?: EmojiNodeDataProvider;
	intl: IntlShape;
}

/**
 * Check if we can nicely fallback to the nodes text
 *
 * @param fallbackText string of the nodes fallback text
 *
 * @example
 * isSingleEmoji('😀') // true
 */
export function isSingleEmoji(fallbackText: string): boolean {
	return SINGLE_EMOJI_REGEX.test(fallbackText);
}

/**
 * Emoji node view for renderering emoji nodes
 */
const EMOJI_TOOLTIP_CLASS = 'emoji-tooltip-editor';
export class EmojiNodeView implements NodeView {
	dom: Node;
	domElement: HTMLElement | undefined;
	private readonly node: PMNode;
	private readonly intl: IntlShape;
	private renderingFallback: boolean = false;
	private tooltipInstance: VanillaTooltip | undefined;
	private tooltipTarget: HTMLElement | undefined;
	private destroyLazyTooltipListeners: (() => void) | undefined;

	readonly destroy = (): void => {
		if (this.tooltipInstance || this.tooltipTarget || this.destroyLazyTooltipListeners) {
			this.destroyTooltip();
		}
	};

	private destroyTooltip(): void {
		this.destroyLazyTooltipListeners?.();
		this.tooltipInstance?.destroy();
		this.tooltipTarget?.removeAttribute('popovertarget');
		this.tooltipTarget?.removeAttribute('aria-describedby');
		this.destroyLazyTooltipListeners = undefined;
		this.tooltipInstance = undefined;
		this.tooltipTarget = undefined;
	}

	private static logError(error: Error) {
		void logException(error, {
			location: 'editor-plugin-emoji/EmojiNodeView',
		});
	}

	/**
	 * Prosemirror node view for rendering emoji nodes. This class is responsible for
	 * rendering emoji nodes in the editor, handling updates, and managing fallback rendering.
	 *
	 * @param node - The ProseMirror node representing the emoji.
	 * @param extraProps - An object containing additional parameters.
	 * @param extraProps.intl - The internationalization object for formatting messages.
	 * @param extraProps.api - The editor API for accessing shared state and connectivity features.
	 * @param extraProps.emojiNodeDataProvider - (Optional) A provider for fetching emoji data.
	 *
	 * @example
	 * const emojiNodeView = new EmojiNodeView(node, { intl, api, emojiNodeDataProvider });
	 */
	constructor(node: PMNode, { intl, api, emojiNodeDataProvider }: Params) {
		this.node = node;
		this.intl = intl;
		const { dom } = DOMSerializer.renderSpec(document, emojiToDom(this.node));
		this.dom = dom;
		this.domElement = this.isHTMLElement(dom) ? dom : undefined;

		if (emojiNodeDataProvider) {
			let previousEmojiDescription: OptionalEmojiDescriptionWithVariations | undefined;

			emojiNodeDataProvider.getData(node, (payload) => {
				if (payload.error) {
					EmojiNodeView.logError(payload.error);
					this.renderFallback();
					return;
				}

				const optionalEmojiDescription = payload.data;

				if (!optionalEmojiDescription) {
					this.renderFallback();
					return;
				}

				const emojiRepresentation = optionalEmojiDescription?.representation;
				if (!EmojiNodeView.isEmojiRepresentationSupported(emojiRepresentation)) {
					this.renderFallback();
					return;
				}

				if (isEqual(previousEmojiDescription, optionalEmojiDescription)) {
					// Do not re-render if the emoji description is the same as before
					return;
				}
				previousEmojiDescription = optionalEmojiDescription;

				this.renderEmoji(optionalEmojiDescription, emojiRepresentation);
			});
		} else {
			if (isSSR()) {
				// The provider doesn't work in SSR, and we don't want to render fallback in SSR,
				// that's why we don't need to continue node rendering.
				// In SSR we want to show a placeholder, that `emojiToDom()` returns.
				return;
			}

			// We use the `emojiProvider` from the shared state
			// because it supports the `emojiProvider` prop in the `ComposableEditor` options
			// as well as the `emojiProvider` in the `EmojiPlugin` options.
			const sharedState = api?.emoji?.sharedState;
			if (!sharedState) {
				return;
			}

			let emojiProvider: EmojiProvider | undefined = sharedState.currentState()?.emojiProvider;
			if (emojiProvider) {
				void this.updateDom(emojiProvider);
			}

			const unsubscribe = sharedState.onChange(({ nextSharedState }) => {
				if (emojiProvider === nextSharedState?.emojiProvider) {
					// Do not update if the provider is the same
					return;
				}

				emojiProvider = nextSharedState?.emojiProvider;
				void this.updateDom(emojiProvider);
			});

			// Refresh emojis if we go back online
			const subscribeToConnection = api?.connectivity?.sharedState.onChange(
				({ prevSharedState, nextSharedState }) => {
					if (
						isOfflineMode(prevSharedState?.mode) &&
						nextSharedState?.mode === 'online' &&
						this.renderingFallback &&
						editorExperiment('platform_editor_offline_editing_web', true)
					) {
						this.updateDom(sharedState.currentState()?.emojiProvider);
					}
				},
			);

			this.destroy = () => {
				unsubscribe();
				subscribeToConnection?.();
				this.destroyTooltip();
			};
		}
	}

	/** Type guard to check if a Node is an HTMLElement in a safe way. */
	private isHTMLElement(element: Node | null): element is HTMLElement {
		if (element === null) {
			return false;
		}

		// In SSR `HTMLElement` is not defined, so we need to use duck typing here
		return 'innerHTML' in element && 'style' in element && 'classList' in element;
	}

	private async updateDom(emojiProvider: EmojiProvider | undefined) {
		try {
			const { shortName, id, text: fallback } = this.node.attrs;

			const emojiDescription = await emojiProvider?.fetchByEmojiId(
				{
					id,
					shortName,
					fallback,
				},
				true,
			);
			if (!emojiDescription) {
				EmojiNodeView.logError(new Error('Emoji description is not loaded'));
				this.renderFallback();
				return;
			}

			const unicodeEmoji =
				id && expValEqualsNoExposure('platform_use_unicode_emojis', 'isEnabled', true)
					? emojiIdToEmoji(id)
					: undefined;
			const emojiRepresentation = unicodeEmoji
				? {
						unicodeEmoji,
					}
				: emojiDescription?.representation;
			if (!EmojiNodeView.isEmojiRepresentationSupported(emojiRepresentation)) {
				EmojiNodeView.logError(new Error('Emoji representation is not supported'));

				this.renderFallback();

				return;
			}

			this.renderEmoji(emojiDescription, emojiRepresentation);
		} catch (error) {
			EmojiNodeView.logError(
				error instanceof Error ? error : new Error('Unknown error on EmojiNodeView updateDom'),
			);

			this.renderFallback();
		}
	}

	private static isEmojiRepresentationSupported(
		representation: EmojiRepresentation,
	): representation is Exclude<EmojiRepresentation, undefined> {
		return (
			!!representation &&
			('sprite' in representation ||
				'imagePath' in representation ||
				'mediaPath' in representation ||
				'unicodeEmoji' in representation)
		);
	}

	private static shouldRecordUnicodeEmojiExposure(
		description: EmojiDescription,
		representation: Exclude<EmojiRepresentation, undefined>,
	): boolean {
		return description.type === 'STANDARD' || 'unicodeEmoji' in representation;
	}

	// Pay attention, this method should be called only when the emoji provider returns
	// emoji data to prevent rendering empty emoji during loading.
	private cleanUpAndRenderCommonAttributes() {
		this.destroyTooltip();

		// Clean up the DOM before rendering the new emoji
		if (this.domElement) {
			this.domElement.innerHTML = '';
			this.domElement.style.cssText = '';
			this.domElement.classList.remove(EmojiSharedCssClassName.EMOJI_PLACEHOLDER);
			this.domElement.removeAttribute('aria-label'); // The label is set in the renderEmoji method
			this.domElement.removeAttribute('aria-busy');
		}
	}

	/**
	 * Lazily creates a VanillaTooltip on the given element showing the emoji shortName.
	 * Gated behind the platform_editor_emoji_hover_show_tooltip experiment.
	 * When the tooltip is active, the native `title` attribute is removed to avoid
	 * showing both the browser tooltip and the custom tooltip.
	 */
	private createTooltip(element: HTMLElement, shortName: string): void {
		if (!expValEquals('platform_editor_emoji_hover_show_tooltip', 'isEnabled', true)) {
			return;
		}

		if (isSSR()) {
			return;
		}

		const initTooltip = (event: Event) => {
			this.destroyLazyTooltipListeners?.();
			this.destroyLazyTooltipListeners = undefined;

			const tooltipId =
				typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
					? `emoji-tooltip-${crypto.randomUUID()}`
					: uniqueId('emoji-tooltip-');
			try {
				// Flag-on (platform-dst-top-layer), positioning is async via a React root;
				// safe here as VanillaTooltip stays hidden until positioned.
				const tooltipInstance = new VanillaTooltip(
					element,
					shortName,
					tooltipId,
					EMOJI_TOOLTIP_CLASS,
					// default timeout
					300,
					// Inline styles are required because the Popover API promotes the tooltip
					// to the browser's top layer, where ancestor CSS selectors cannot reach it.
					{
						boxSizing: 'border-box',
						maxWidth: '240px',
						backgroundColor: token('color.background.neutral.bold'),
						border: 'none',
						borderRadius: token('radius.small', '3px'),
						color: token('color.text.inverse'),
						font: token('font.body.small'),
						fontFamily: token('font.family.body'),
						insetBlockStart: token('space.0', '0px'),
						insetInlineStart: token('space.0', '0px'),
						overflowWrap: 'break-word',
						paddingBlockStart: token('space.050', '4px'),
						paddingBlockEnd: token('space.050', '4px'),
						paddingInlineEnd: token('space.075', '6px'),
						paddingInlineStart: token('space.075', '6px'),
						whiteSpace: 'normal',
					},
				);

				this.tooltipInstance = tooltipInstance;
				this.tooltipTarget = element;
				element.removeAttribute('title');
				element.dispatchEvent(new Event(event.type));
			} catch (error) {
				element.removeAttribute('popovertarget');
				element.removeAttribute('aria-describedby');
				EmojiNodeView.logError(error instanceof Error ? error : new Error(String(error)));
			}
		};

		const unbindMouseEnter = bind(element, {
			type: 'mouseenter',
			listener: initTooltip,
			options: { once: true },
		});
		const unbindFocus = bind(element, {
			type: 'focus',
			listener: initTooltip,
			options: { once: true },
		});
		this.destroyLazyTooltipListeners = () => {
			unbindMouseEnter();
			unbindFocus();
		};
	}

	private renderFallback() {
		this.renderingFallback = true;
		this.cleanUpAndRenderCommonAttributes();

		let doc = getDocument();
		if (!doc) {
			// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
			doc = document;
		}
		const fallbackElement = doc.createElement('span');

		const { text, shortName } = this.node.attrs;

		// When the gate is enabled and the emoji is "custom" (i.e. its fallback
		// text is not a single standard Unicode emoji), render the Unicode
		// Replacement Character (U+FFFD) instead of the shortName text. Standard
		// emojis continue to fall back to their Unicode text representation.
		const fallbackText = text || shortName;
		const useReplacementChar =
			fg('platform_editor_custom_emoji_unicode_fallback') && !isSingleEmoji(fallbackText);
		const renderedFallbackText = useReplacementChar ? '\uFFFD' : fallbackText;

		fallbackElement.innerText = renderedFallbackText;
		fallbackElement.setAttribute('role', 'img');
		fallbackElement.setAttribute('title', shortName);
		fallbackElement.setAttribute('aria-label', shortName);
		fallbackElement.setAttribute('data-testid', `fallback-emoji-${shortName}`);
		fallbackElement.setAttribute('data-emoji-type', 'fallback');

		this.dom.appendChild(fallbackElement);
		this.createTooltip(fallbackElement, shortName);
	}

	private renderEmoji(
		description: EmojiDescription,
		representation: Exclude<EmojiRepresentation, undefined>,
	) {
		this.renderingFallback = false;
		this.cleanUpAndRenderCommonAttributes();

		const emojiType =
			'unicodeEmoji' in representation
				? 'unicode'
				: 'sprite' in representation
					? 'sprite'
					: 'image';

		let doc = getDocument();
		if (!doc) {
			// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
			doc = document;
		}

		// Add wrapper for the emoji
		const containerElement = doc.createElement('span');
		containerElement.setAttribute('role', 'img');
		containerElement.setAttribute('title', description.shortName);
		containerElement.classList.add(EmojiSharedCssClassName.EMOJI_CONTAINER);
		containerElement.setAttribute('data-testid', `${emojiType}-emoji-${description.shortName}`);
		containerElement.setAttribute('data-emoji-type', emojiType);
		containerElement.setAttribute(
			'aria-label',
			`${this.intl.formatMessage(messages.emojiNodeLabel)} ${description.shortName}`,
		);

		const emojiElement =
			'unicodeEmoji' in representation
				? this.createUnicodeEmojiElement(representation.unicodeEmoji)
				: 'sprite' in representation
					? this.createSpriteEmojiElement(representation)
					: this.createImageEmojiElement(description, representation);

		containerElement.appendChild(emojiElement);

		this.dom.appendChild(containerElement);

		if (EmojiNodeView.shouldRecordUnicodeEmojiExposure(description, representation)) {
			expValEquals('platform_use_unicode_emojis', 'isEnabled', true);
		}

		this.createTooltip(containerElement, description.shortName);
	}

	private createUnicodeEmojiElement(emoji: string): HTMLSpanElement {
		let doc = getDocument();
		if (!doc) {
			// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
			doc = document;
		}
		const spanElement = doc.createElement('span');

		spanElement.classList.add(EmojiSharedCssClassName.EMOJI_UNICODE);
		spanElement.textContent = emoji;
		spanElement.style.display = 'inline-flex';
		spanElement.style.fontSize = `var(--emoji-common-unicode-size, ${defaultEmojiHeight}px)`;
		spanElement.style.alignItems = 'center';
		spanElement.style.aspectRatio = '1/1';
		spanElement.style.lineHeight = '1em';
		spanElement.style.margin = '-1px 0';
		spanElement.style.verticalAlign = 'middle'; // to keep vertical alignment consistent with images

		return spanElement;
	}

	private createSpriteEmojiElement(representation: SpriteRepresentation): HTMLSpanElement {
		let doc = getDocument();
		if (!doc) {
			// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
			doc = document;
		}
		const spriteElement = doc.createElement('span');

		spriteElement.classList.add(EmojiSharedCssClassName.EMOJI_SPRITE);

		const sprite = representation.sprite;

		const xPositionInPercent = (100 / (sprite.column - 1)) * representation.xIndex;
		const yPositionInPercent = (100 / (sprite.row - 1)) * representation.yIndex;

		spriteElement.style.backgroundImage = `url(${sprite.url})`;
		spriteElement.style.backgroundPosition = `${xPositionInPercent}% ${yPositionInPercent}%`;
		spriteElement.style.backgroundSize = `${sprite.column * 100}% ${sprite.row * 100}%`;
		spriteElement.style.minWidth = `${defaultEmojiHeight}px`;
		spriteElement.style.minHeight = `${defaultEmojiHeight}px`;

		if (!expValEquals('platform_editor_lovability_emoji_scaling', 'isEnabled', true)) {
			spriteElement.style.width = `${defaultEmojiHeight}px`;
			spriteElement.style.height = `${defaultEmojiHeight}px`;
		}
		return spriteElement;
	}

	private createImageEmojiElement(
		emojiDescription: EmojiDescription,
		representation: ImageRepresentation | MediaApiRepresentation,
	): HTMLImageElement {
		let doc = getDocument();
		if (!doc) {
			// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
			doc = document;
		}
		const imageElement = doc.createElement('img');

		imageElement.classList.add(EmojiSharedCssClassName.EMOJI_IMAGE);

		imageElement.src =
			'imagePath' in representation ? representation.imagePath : representation.mediaPath;
		imageElement.loading = 'lazy';
		imageElement.alt = emojiDescription.name || emojiDescription.shortName;

		imageElement.style.minWidth = `${defaultEmojiHeight}px`;
		imageElement.style.objectFit = 'contain';
		imageElement.height = defaultEmojiHeight;

		imageElement.onerror = () => {
			if (editorExperiment('platform_editor_offline_editing_web', true)) {
				// If there's an error (ie. offline) render the ascii fallback if possible, otherwise
				// mark the node to refresh when returning online.
				// Create a check that confirms if this.node.attrs.text if an ascii emoji
				if (isSingleEmoji(this.node.attrs.text)) {
					this.renderFallback();
				} else {
					this.renderingFallback = true;
				}
			} else {
				this.renderFallback();
			}
		};

		return imageElement;
	}
}
